import {
  CloseCircleOutlined,
  DislikeOutlined,
  EditOutlined,
  HighlightOutlined,
  HourglassOutlined,
  LikeOutlined,
  QuestionCircleOutlined,
  SmileOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { CSSProperties, ReactNode } from 'react';

import { ConsentStatusType } from '../../../types';

// "Etapa Impedida" do sinaleiro do PSP é uma mão espalmada; o antd 4 não tem.
function ImpededPhaseIcon({ style }: { style?: CSSProperties }) {
  return (
    <span
      role="img"
      aria-label="etapa impedida"
      className="anticon"
      style={style}
    >
      <svg
        viewBox="0 0 24 24"
        width="1em"
        height="1em"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 11V6a2 2 0 0 0-4 0v5" />
        <path d="M14 10V4a2 2 0 0 0-4 0v6" />
        <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
      </svg>
    </span>
  );
}

type StatusConfig = {
  label: string;
  color: string;
  render: (style: CSSProperties) => ReactNode;
};

// Legenda do sinaleiro do PSP, na mesma ordem. Cores próximas às do PSP.
export const CONSENT_STATUS_CONFIG: Record<ConsentStatusType, StatusConfig> = {
  PHASE_IMPEDED: {
    label: 'Etapa Impedida',
    color: '#f5222d',
    render: (style) => <ImpededPhaseIcon style={style} />,
  },
  PROVIDED: {
    label: 'Anuência Fornecida',
    color: '#52c41a',
    render: (style) => <LikeOutlined style={style} />,
  },
  AWAITING_CONSENT: {
    label: 'Aguardando Anuência',
    color: '#1677ff',
    render: (style) => <EditOutlined style={style} />,
  },
  DENIED: {
    label: 'Anuência Negada',
    color: '#f5222d',
    render: (style) => <DislikeOutlined style={style} />,
  },
  AWAITING_REQUIREMENT: {
    label: 'Aguardando Cumprimento de Exigências',
    color: '#fa8c16',
    render: (style) => <StopOutlined style={style} />,
  },
  DEFAULT: {
    label: 'Anuência Padrão',
    color: '#52c41a',
    render: (style) => <SmileOutlined style={style} />,
  },
  PHASE_SUSPENDED: {
    label: 'Etapa Suspensa',
    color: '#cf1322',
    render: (style) => <CloseCircleOutlined style={style} />,
  },
  AWAITING_REQUIREMENT_REVIEW: {
    label: 'Aguardando análise do cumprimento de exigência',
    color: '#1677ff',
    render: (style) => <HourglassOutlined style={style} />,
  },
  AWAITING_NEW_REQUEST: {
    label: 'Aguardando Anuência - Nova Solicitação',
    color: '#69b1ff',
    render: (style) => <HighlightOutlined style={style} />,
  },
  UNKNOWN: {
    label: 'Situação não reconhecida ou sem dados do PSP',
    color: '#8c8c8c',
    render: (style) => <QuestionCircleOutlined style={style} />,
  },
};

export const CONSENT_LEGEND_ORDER: ConsentStatusType[] = [
  'PHASE_IMPEDED',
  'PROVIDED',
  'AWAITING_CONSENT',
  'DENIED',
  'AWAITING_REQUIREMENT',
  'DEFAULT',
  'PHASE_SUSPENDED',
  'AWAITING_REQUIREMENT_REVIEW',
  'AWAITING_NEW_REQUEST',
  'UNKNOWN',
];

type ConsentSignalIconProps = {
  status?: ConsentStatusType | null;
  size?: number;
  // Texto do tooltip; sem ele, o rótulo da legenda.
  tooltip?: ReactNode;
  showTooltip?: boolean;
  style?: CSSProperties;
};

export function ConsentSignalIcon(props: ConsentSignalIconProps) {
  const { status, size = 18, tooltip, showTooltip = true, style } = props;
  if (!status) return null;

  const config = CONSENT_STATUS_CONFIG[status] || CONSENT_STATUS_CONFIG.UNKNOWN;
  const icon = config.render({ color: config.color, fontSize: size, ...style });

  if (!showTooltip) return icon as JSX.Element;
  return <Tooltip title={tooltip ?? config.label}>{icon}</Tooltip>;
}

export function consentStatusLabel(status?: ConsentStatusType | null) {
  if (!status) return '';
  return (CONSENT_STATUS_CONFIG[status] || CONSENT_STATUS_CONFIG.UNKNOWN).label;
}
